/**
 * Shared auth form used by both AuthModal (landing page overlay) and
 * SignInPage (full-page fallback for unauthenticated non-root routes).
 *
 * Handles Google, Apple, and email/password sign-in and sign-up. The
 * caller controls which mode opens by default via `initialMode`.
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePlausible } from "@/hooks/usePlausible";

type Mode = "sign-in" | "sign-up" | "forgot-password";

interface AuthFormProps {
  initialMode?: Mode;
  /** Called on each mode change — useful for parent headers that reflect mode. */
  onModeChange?: (mode: Mode) => void;
}

export function AuthForm({ initialMode = "sign-in", onModeChange }: AuthFormProps) {
  const { signInWithGoogle, signInWithApple, signInWithEmail, signUp, sendPasswordReset } = useAuth();
  const { trackEvent } = usePlausible();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Keep local mode in sync when the parent re-opens with a different initialMode.
  useEffect(() => {
    setMode(initialMode);
    setError(null);
    setResetSent(false);
  }, [initialMode]);

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setResetSent(false);
    onModeChange?.(next as "sign-in" | "sign-up");
  }

  function cleanError(err: unknown) {
    const msg = err instanceof Error ? err.message : "Something went wrong";
    return msg.replace("Firebase: ", "").replace(/\(auth\/.*?\)\.?/, "").trim();
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "sign-in") await signInWithEmail(email, password);
      else { await signUp(email, password); trackEvent("Sign Up"); }
    } catch (err) {
      setError(cleanError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordReset(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await sendPasswordReset(email);
      setResetSent(true);
    } catch (err) {
      setError(cleanError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    try { await signInWithGoogle(); }
    catch (err) { setError(cleanError(err)); }
  }

  async function handleApple() {
    setError(null);
    try { await signInWithApple(); }
    catch (err) { setError(cleanError(err)); }
  }

  // ── Forgot password view ──────────────────────────────────────────────────
  if (mode === "forgot-password") {
    if (resetSent) {
      return (
        <div className="space-y-5">
          <div className="rounded-2xl bg-stone-50 px-5 py-5 text-center">
            <p className="text-sm font-medium text-stone-800">Check your inbox</p>
            <p className="mt-1 text-xs text-stone-500">
              We sent a reset link to <span className="font-medium text-stone-700">{email}</span>.
              It may take a minute to arrive.
            </p>
          </div>
          <button
            onClick={() => switchMode("sign-in")}
            className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-700 transition hover:bg-stone-50"
          >
            Back to sign in
          </button>
        </div>
      );
    }

    return (
      <div>
        <p className="mb-5 text-sm text-stone-500">
          Enter your email and we'll send you a link to reset your password.
        </p>
        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-stone-600">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-2.5 text-sm text-stone-900 placeholder-stone-400 outline-none transition focus:border-stone-400 focus:bg-white"
            />
          </div>

          {error && (
            <p className="rounded-xl bg-red-50 px-4 py-2.5 text-xs text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:opacity-50"
          >
            {loading ? "…" : "Send reset link"}
          </button>
        </form>

        <p className="mt-5 text-center text-xs text-stone-500">
          <button
            onClick={() => switchMode("sign-in")}
            className="font-medium text-stone-900 underline underline-offset-2"
          >
            Back to sign in
          </button>
        </p>
      </div>
    );
  }

  // ── Sign in / Sign up view ────────────────────────────────────────────────
  return (
    <div>
      <div className="space-y-3">
        <button
          onClick={handleGoogle}
          disabled={loading}
          className="flex w-full items-center justify-center gap-3 rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm font-medium text-stone-700 transition hover:bg-stone-50 hover:border-stone-300 disabled:opacity-50"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>
      </div>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-stone-200" />
        <span className="text-xs text-stone-400">or</span>
        <div className="h-px flex-1 bg-stone-200" />
      </div>

      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-stone-600">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-2.5 text-sm text-stone-900 placeholder-stone-400 outline-none transition focus:border-stone-400 focus:bg-white"
          />
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-xs font-medium text-stone-600">Password</label>
            {mode === "sign-in" && (
              <button
                type="button"
                onClick={() => switchMode("forgot-password")}
                className="text-xs text-stone-400 underline underline-offset-2 hover:text-stone-600"
              >
                Forgot password?
              </button>
            )}
          </div>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-2.5 text-sm text-stone-900 placeholder-stone-400 outline-none transition focus:border-stone-400 focus:bg-white"
          />
        </div>

        {error && (
          <p className="rounded-xl bg-red-50 px-4 py-2.5 text-xs text-red-600">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:opacity-50"
        >
          {loading ? "…" : mode === "sign-in" ? "Sign in" : "Create account"}
        </button>
      </form>

      <p className="mt-5 text-center text-xs text-stone-500">
        {mode === "sign-in" ? (
          <>
            No account?{" "}
            <button
              onClick={() => switchMode("sign-up")}
              className="font-medium text-stone-900 underline underline-offset-2"
            >
              Sign up
            </button>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <button
              onClick={() => switchMode("sign-in")}
              className="font-medium text-stone-900 underline underline-offset-2"
            >
              Sign in
            </button>
          </>
        )}
      </p>
    </div>
  );
}
